import { isProgressNotePlaceholder } from './progressNoteBody';

function hasText(value) {
  return typeof value === 'string' && value.trim().length > 0;
}

/** True if text looks like model self-talk rather than clinical content. */
export function isLlmReasoningText(text) {
  const value = String(text || '').trim();
  if (!value) return false;
  return (
    /\b(i'?ll keep(?:\s+it)?\s+as written|keep as written|context unclear|was not said|on second thought|let me (?:think|reconsider)|wait\s*[-—–:]|likely\s+["“][^"”]+["”]\s+was|i (?:think|believe|assume|guess)\s+(?:this|that|it)|probably meant|not sure (?:if|whether)|unclear (?:if|whether|what|context))\b/i.test(
      value
    )
    || /^(?:no|yes)\s*[—–-]\s*likely\b/i.test(value)
  );
}

/**
 * Drop "Not documented" placeholders and leaked LLM reasoning from handover bodies.
 */
export function stripHandoverPlaceholders(text) {
  if (typeof text !== 'string' || !text) return '';

  let cleaned = text
    // Pipe asides: "Atenolol? |No — likely "ATEN" was not said..."
    .replace(/\s*\|\s*[^\n]+/g, (aside) =>
      /likely|was not said|keep as written|context unclear|\bwait\b|actually\b|probably|i'?ll keep/i.test(
        aside
      )
        ? ''
        : aside
    );

  cleaned = cleaned
    .split(/\n/)
    .map((line) => {
      const trimmed = line.trim();
      if (!trimmed) return '';
      if (isProgressNotePlaceholder(trimmed) || isLlmReasoningText(trimmed)) return '';

      const kept = trimmed
        .split(/(?<=[.!?])\s+/)
        .map((part) => part.trim())
        .filter((part) => part && !isProgressNotePlaceholder(part) && !isLlmReasoningText(part));
      return kept.join(' ');
    })
    .filter((line, index, arr) => line || (index > 0 && index < arr.length - 1 && arr[index - 1]))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+\n/g, '\n')
    .trim();

  return cleaned;
}

function cleanJoinedSections(sections) {
  return sections
    .map((value) => String(value || '').trim())
    .filter((value) => value && !isProgressNotePlaceholder(value))
    .join('\n\n');
}

/** Free-text body for one handover patient entry (replaces SBAR UI). */
export function getHandoverEntryBody(entry) {
  if (!entry) return '';
  if (typeof entry.narrative === 'string' && entry.narrative.length > 0) {
    return stripHandoverPlaceholders(entry.narrative);
  }
  const sbar = entry.sbar || {};
  return cleanJoinedSections([
    sbar.situation,
    sbar.background,
    sbar.assessment,
    sbar.recommendation,
  ]);
}

export function setHandoverEntryBody(entry, text) {
  const value = typeof text === 'string' ? text : '';
  return {
    ...(entry || {}),
    narrative: value,
    sbar: {
      situation: value,
      background: '',
      assessment: '',
      recommendation: '',
    },
  };
}

/** Free-text body for a handover note (single or multi-patient first entry fallback). */
export function getHandoverNoteBody(note) {
  if (!note) return '';
  if (typeof note.narrative === 'string' && note.narrative.length > 0) {
    return stripHandoverPlaceholders(note.narrative);
  }
  if (note.sbar) {
    const fromSbar = cleanJoinedSections([
      note.sbar.situation,
      note.sbar.background,
      note.sbar.assessment,
      note.sbar.recommendation,
    ]);
    if (fromSbar) return fromSbar;
  }
  const patients = note.patients || [];
  if (patients.length === 1) return getHandoverEntryBody(patients[0]);
  return typeof note.narrative === 'string' ? stripHandoverPlaceholders(note.narrative) : '';
}

export function setHandoverNoteBody(note, text) {
  const value = typeof text === 'string' ? text : '';
  const next = {
    ...(note || {}),
    note_type: 'handover',
    narrative: value,
    sbar: {
      situation: value,
      background: '',
      assessment: '',
      recommendation: '',
    },
  };

  if (Array.isArray(note?.patients) && note.patients.length) {
    next.patients = note.patients.map((entry, index) =>
      index === 0 ? setHandoverEntryBody(entry, value) : entry
    );
  }

  return next;
}

export function handoverNoteHasContent(note) {
  if (!note) return false;
  if (hasText(getHandoverNoteBody(note))) return true;
  return (note.patients || []).some((entry) => hasText(getHandoverEntryBody(entry)));
}
